import { Component, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RoleService } from '../../core/services/role.service';
import { ApiService } from '../../core/services/api.service';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { forkJoin } from 'rxjs';
import { ToastService } from '../../account/login/toast-service';

declare var google: any;


@Component({
  selector: 'app-incident-create',
  templateUrl: './incident-create.component.html',
  styleUrls: ['./incident-create.component.scss'],
  standalone: false
})
export class IncidentCreateComponent implements OnInit, AfterViewInit {
  incidentForm!: FormGroup;
  submitted = false;
  loading = false;
  
  @ViewChild('locationInput') locationInput!: ElementRef;
  
  private autocomplete: any;
  
  severities: any[] = [];
  zones: any[] = [];
  wards: any[] = [];
  loadingDropdowns = false;

  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private router: Router,
    public roleService: RoleService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadDropdownData();
  }

  ngAfterViewInit(): void {
    this.initializeAutocomplete();
  }

  private initializeAutocomplete(): void {
      const autocomplete = new google.maps.places.Autocomplete(this.locationInput.nativeElement, {
        types: ['geocode', 'establishment'],
        fields: ['formatted_address', 'geometry', 'name', 'address_components']
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          // Update form with selected place
          this.incidentForm.patchValue({
            location: place.formatted_address || place.name,
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng()
          });

        }
      });

      this.autocomplete = autocomplete;
  }

   loadDropdownData(): void {
    this.loadingDropdowns = true;
    
    // Load severities and zones initially
    forkJoin({
      severities: this.apiService.get('/severities'),
      zones: this.apiService.get('/zone')
    }).subscribe({
      next: (response: any) => {
        this.severities = response.severities.data || [];
        this.zones = response.zones.data || [];
        this.loadingDropdowns = false;
      },
      error: (error) => {
        console.error('Error loading dropdown data:', error);
        this.loadingDropdowns = false;
        Swal.fire({
          icon: 'warning',
          title: 'Data Loading Error',
          text: 'Unable to load dropdown options. Please refresh the page.',
          confirmButtonColor: '#405189'
        });
      }
    });
  }


  initForm(): void {
    this.incidentForm = this.formBuilder.group({
      contactNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      contactName: ['', Validators.required],
      remarks: [''],
      severityId: ['', Validators.required],
      location: ['', Validators.required],
      landmark: [''],
      latitude: [null],
      longitude: [null]
    });
  }

  get f() {
    return this.incidentForm.controls;
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.incidentForm.invalid) {
      return;
    }

    this.loading = true;
    const incidentData = {
      contactNumber: this.incidentForm.value.contactNumber,
      contactName: this.incidentForm.value.contactName,
      remarks: this.incidentForm.value.remarks,
      severityId: parseInt(this.incidentForm.value.severityId),
      location: this.incidentForm.value.location,
      landmark: this.incidentForm.value.landmark || '',
      latitude: parseFloat(this.incidentForm.value.latitude) || 0,
      longitude: parseFloat(this.incidentForm.value.longitude) || 0
    };

    this.apiService.post('/incidents', incidentData).subscribe({
      next: (response) => {
        this.toastService.show('Incident reported successfully!', { 
          classname: 'bg-success text-white', 
          delay: 3000 
        });
        this.loading = false;
        
        // Reset form after successful submission
        this.incidentForm.reset();
        this.submitted = false;
        
        // Navigate to incidents list after a brief delay
        setTimeout(() => {
          this.router.navigate(['/incidents']);
        }, 1000);
      },
      error: (error) => {
        console.error('Error creating incident:', error);
        this.toastService.show('Failed to report incident. Please try again.', { 
          classname: 'bg-danger text-white', 
          delay: 5000 
        });
        this.loading = false;
      }
    });
  }

  resetForm(): void {
    this.submitted = false;
    this.incidentForm.reset();
    this.incidentForm.patchValue({
      severityId: ''
    });
  }
}
